import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Typography,
} from '@mui/material'
import { getLocaleFromFullDate } from 'common/libs/fechas'

const getProgressColor = (value) => {
  if (value >= 75) return 'success'
  if (value >= 33) return 'warning'
  return 'error'
}

const EvaluacionCard = ({ evaluacion, onClick, loading }) => {
  if (loading) {
    return (
      <Card sx={{ borderRadius: 2, minHeight: 240, width: '100%', minWidth: 362 }}>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="rounded" width={70} height={24} />
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="50%" height={16} />
              <Skeleton variant="text" width="80%" height={24} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="50%" height={16} />
              <Skeleton variant="text" width="80%" height={24} />
            </Box>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Skeleton variant="circular" width={72} height={72} />
          </Box>
          <Skeleton variant="text" width="50%" height={16} sx={{ mx: 'auto' }} />
          <Skeleton variant="text" width="35%" height={16} sx={{ mx: 'auto', mb: 1 }} />
        </CardContent>
      </Card>
    )
  }

  if (!evaluacion) {
    return <Typography variant="body2">No hay evaluaciones disponibles.</Typography>
  }

  const descripcion = (evaluacion.descripcion ?? '').trim()
  const porcentaje = Math.round(parseFloat(evaluacion.n_evaluaciones) || 0)
  const nTotal = evaluacion.n_total ?? null
  const nCerradas = Math.round(parseFloat(evaluacion.n_cerradas) || 0)
  const nPendientes = nTotal !== null ? nTotal - nCerradas : null

  const chipLabel =
    porcentaje === 100 ? 'Completada' : porcentaje > 0 ? 'En progreso' : 'Sin iniciar'
  const chipColor = porcentaje === 100 ? 'success' : porcentaje > 0 ? 'primary' : 'default'
  const progressColor = porcentaje > 0 ? getProgressColor(porcentaje) : 'inherit'

  return (
    <Card
      sx={{
        borderRadius: 2,
        minHeight: 240,
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': { boxShadow: 6, transform: 'translateY(-3px)' },
      }}
    >
      <CardActionArea onClick={() => onClick(evaluacion.id)}>
        <CardHeader
          title={descripcion}
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
          action={<Chip label={chipLabel} color={chipColor} size="small" sx={{ mt: 1, mr: 1 }} />}
        />
        <CardContent sx={{ pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="overline"
                display="block"
                color="text.secondary"
                lineHeight={1.5}
              >
                Proceso
              </Typography>
              <Typography variant="body2">
                {evaluacion.fecha_proceso
                  ? getLocaleFromFullDate(evaluacion.fecha_proceso, false)
                  : '—'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="overline"
                display="block"
                color="text.secondary"
                lineHeight={1.5}
              >
                Validez
              </Typography>
              <Typography variant="body2">
                {evaluacion.fecha_validez_est
                  ? getLocaleFromFullDate(evaluacion.fecha_validez_est, false)
                  : '—'}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}
            >
              {nTotal !== null && (
                <Typography variant="caption" color="text.secondary">
                  {nCerradas} de {nTotal} completadas
                </Typography>
              )}
              {nPendientes !== null && nPendientes > 0 && (
                <Typography variant="caption" color={`${getProgressColor(porcentaje)}.main`}>
                  {nPendientes} pendiente{nPendientes !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={porcentaje}
                color={progressColor}
                size={72}
                thickness={4}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" fontWeight="bold" color={`${progressColor}.main`}>
                  {`${porcentaje}%`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default EvaluacionCard
